import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Building2, UserRound, Layers, Users, CheckCircle2, Copy, Upload, ArrowRight, ArrowLeft, PartyPopper, QrCode } from 'lucide-react';
import { PatientIntakeQR } from '@/components/PatientIntakeQR';

const STEPS = [
  { id: 1, label: 'Your account', icon: UserRound },
  { id: 2, label: 'Practice', icon: Building2 },
  { id: 3, label: 'Procedure catalog', icon: Layers },
  { id: 4, label: 'Invite your team', icon: Users },
  // §2 admin note (2026-09-06): signup ended at "Invite your team" and never
  // showed the patient link — the one thing a practice needs on day one.
  { id: 5, label: 'Patient link', icon: QrCode },
];

export default function Signup() {
  const { user, signup, authLoading } = useApp();
  const navigate = useNavigate();
  // ADR 0008: an office invited by a patient's request arrives with its claim
  // token (and the address the patient used); signing up puts that request in
  // the new practice's Patient requests list.
  const [params] = useSearchParams();
  const claimToken = params.get('claim') || '';
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState({ name: '', email: params.get('email') || '', password: '', confirm: '' });
  const [practiceForm, setPracticeForm] = useState({ practice_name: '', practice_address: '', billing_email: '' });
  // Whether the Terms/BAA are actually in force, straight from the server —
  // the same fail-closed pattern useDemoLaunch uses. While they are drafts the
  // server refuses to record an acceptance, so asking for one here would be
  // theatre: `legal.required` comes back empty and no checkbox renders.
  const [legal, setLegal] = useState({ required: [], documents: {} });
  const [accepted, setAccepted] = useState({});

  // If already signed in (e.g. refreshed mid-wizard), resume at catalog step
  useEffect(() => {
    if (!authLoading && user && step < 3) {
      if (user.role === 'admin') setStep(3);
      else navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  useEffect(() => {
    let alive = true;
    api.get('/')
      .then((r) => { if (alive) setLegal(r.data?.legal || { required: [], documents: {} }); })
      // Unreachable API: ask for nothing. A missed checkbox is recoverable —
      // the server rejects the signup and the prospect retries. Recording
      // assent we could not verify is not.
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const setA = (k) => (e) => setAccount((f) => ({ ...f, [k]: e.target.value }));
  const setP = (k) => (e) => setPracticeForm((f) => ({ ...f, [k]: e.target.value }));

  const nextFromAccount = (e) => {
    e.preventDefault();
    if (account.password.length < 10 || !/[A-Za-z]/.test(account.password) || !/\d/.test(account.password)) { toast.error('Password must be at least 10 characters and include a letter and a number'); return; }
    if (account.password !== account.confirm) { toast.error('Passwords do not match'); return; }
    setStep(2);
  };

  const unaccepted = (legal.required || []).filter((k) => !accepted[k]);

  const createWorkspace = async (e) => {
    e.preventDefault();
    if (unaccepted.length) {
      toast.error(`Please accept the ${unaccepted.map((k) => legal.documents?.[k]?.title || k).join(' and ')} to continue`);
      return;
    }
    setBusy(true);
    try {
      await signup({
        name: account.name, email: account.email, password: account.password,
        practice_name: practiceForm.practice_name,
        practice_address: practiceForm.practice_address,
        billing_email: practiceForm.billing_email,
        accept_terms: !!accepted.terms,
        accept_baa: !!accepted.baa,
        ...(claimToken ? { claim_token: claimToken } : {}),
      });
      toast.success('Workspace created!');
      setStep(3);
    } catch (err) {
      toast.error(errText(err, 'Could not create workspace.'));
    } finally {
      setBusy(false);
    }
  };

  const progress = (step / STEPS.length) * 100;

  return (
    <div className="min-h-screen hero-wash">
      <div className="noise min-h-screen flex flex-col">
        <header className="h-14 flex items-center px-4 lg:px-6 justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="signup-logo">
            <div className="h-7 w-7 rounded-lg bg-primary text-primary-foreground grid place-items-center font-bold">M</div>
            <span className="font-semibold tracking-tight">DoctorPaperwork</span>
          </Link>
          <span className="text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-primary font-medium hover:underline" data-testid="signup-login-link">Sign in</Link>
          </span>
        </header>

        <div className="flex-1 px-4 py-8">
          <div className="max-w-2xl mx-auto space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                {STEPS.map((s) => {
                  const I = s.icon;
                  const state = step > s.id ? 'done' : step === s.id ? 'active' : 'todo';
                  return (
                    <div key={s.id} className="flex items-center gap-1.5" data-testid={`signup-step-indicator-${s.id}`}>
                      <div className={`h-7 w-7 rounded-full grid place-items-center text-xs font-semibold ${state === 'done' ? 'bg-primary text-primary-foreground' : state === 'active' ? 'bg-accent text-accent-foreground border border-primary' : 'bg-secondary text-muted-foreground'}`}>
                        {state === 'done' ? <CheckCircle2 className="h-4 w-4" /> : <I className="h-3.5 w-3.5" />}
                      </div>
                      <span className={`hidden sm:inline text-xs ${state === 'active' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
                    </div>
                  );
                })}
              </div>
              <Progress value={progress} className="h-1.5" data-testid="signup-progress" />
            </div>

            {step === 1 && (
              <Card className="shadow-lg shadow-black/5">
                <CardHeader>
                  <CardTitle className="text-xl">Create your account</CardTitle>
                  <CardDescription>You will be the practice administrator. You can invite doctors and nurses in a moment.</CardDescription>
                  {claimToken && (
                    <p className="text-sm rounded-lg bg-secondary p-3 mt-2" data-testid="signup-claim-note">
                      A patient’s request will be waiting under <b>Patient requests</b> once your account is set up.
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <form onSubmit={nextFromAccount} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Your full name</Label>
                      <Input required value={account.name} onChange={setA('name')} placeholder="Riley Park" data-testid="signup-name-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Work email</Label>
                      <Input required type="email" value={account.email} onChange={setA('email')} placeholder="you@yourpractice.com" data-testid="signup-email-input" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Password</Label>
                        <Input required type="password" value={account.password} onChange={setA('password')} placeholder="At least 10 characters, with a letter and a number" data-testid="signup-password-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Confirm password</Label>
                        <Input required type="password" value={account.confirm} onChange={setA('confirm')} placeholder="Repeat password" data-testid="signup-confirm-input" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full" data-testid="signup-step1-continue">Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {step === 2 && (
              <Card className="shadow-lg shadow-black/5">
                <CardHeader>
                  <CardTitle className="text-xl">Tell us about your practice</CardTitle>
                  <CardDescription>This appears on generated documents and patient-facing pages.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createWorkspace} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Practice name</Label>
                      <Input required value={practiceForm.practice_name} onChange={setP('practice_name')} placeholder="Lakeside Orthopedic Clinic" data-testid="signup-practice-name-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Practice address</Label>
                      <Input value={practiceForm.practice_address} onChange={setP('practice_address')} placeholder="123 Main St, Suite 200, Springfield, IL" data-testid="signup-practice-address-input" />
                      <p className="text-xs text-muted-foreground">Printed on signed documentation.</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Billing email (optional)</Label>
                      <Input type="email" value={practiceForm.billing_email} onChange={setP('billing_email')} placeholder="billing@yourpractice.com" data-testid="signup-billing-email-input" />
                    </div>
                    {(legal.required || []).length > 0 && (
                      <div className="space-y-2.5 rounded-lg border bg-muted/40 p-3" data-testid="signup-legal-block">
                        {legal.required.map((key) => {
                          const doc = legal.documents?.[key] || {};
                          return (
                            <label key={key} className="flex cursor-pointer items-start gap-2.5 text-xs leading-relaxed">
                              <Checkbox
                                checked={!!accepted[key]}
                                onCheckedChange={(v) => setAccepted((a) => ({ ...a, [key]: !!v }))}
                                className="mt-0.5"
                                data-testid={`signup-accept-${key}`}
                              />
                              <span className="text-muted-foreground">
                                {key === 'baa' ? 'I am authorized to bind this practice, and I accept the ' : 'I accept the '}
                                <Link to={doc.path || '/'} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline">
                                  {doc.title || key}
                                </Link>
                                {doc.version ? <span className="text-muted-foreground/70"> (version {doc.version})</span> : null}
                                {key === 'baa' && '. HIPAA requires this before any patient information may be submitted.'}
                                {key === 'terms' && '.'}
                              </span>
                            </label>
                          );
                        })}
                        <p className="text-[0.7rem] text-muted-foreground/80">
                          We record who accepted, when, and which version, and you can download an executed copy from
                          Settings. Prefer a countersigned paper copy? Create the account and ask us — most compliance
                          teams do.
                        </p>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" onClick={() => setStep(1)} data-testid="signup-step2-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
                      <Button type="submit" className="flex-1" disabled={busy || unaccepted.length > 0} data-testid="signup-step2-create">
                        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create workspace <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {step === 3 && <CatalogStep onNext={() => setStep(4)} />}
            {step === 4 && <TeamStep onFinish={() => setStep(5)} />}
            {step === 5 && <PatientLinkStep onFinish={() => navigate(claimToken ? '/patient-requests' : '/admin', { replace: true })} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function CatalogStep({ onNext }) {
  const [templates, setTemplates] = useState([]);
  const [applied, setApplied] = useState([]);
  const [busyId, setBusyId] = useState('');
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/catalog/templates').then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const apply = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/catalog/templates/${id}/apply`);
      setApplied((a) => [...a, id]);
      toast.success(`${r.data.template}: ${r.data.added} procedures added`);
    } catch (e) {
      toast.error(errText(e, 'Could not apply template'));
    } finally {
      setBusyId('');
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const r = await api.post('/catalog/import', { csv: text });
      toast.success(`CSV imported: ${r.data.added} added, ${r.data.updated} updated${r.data.failed_count ? `, ${r.data.failed_count} failed` : ''}`);
      setApplied((a) => [...a, 'csv']);
    } catch (e) {
      toast.error(errText(e, 'CSV import failed'));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/catalog/csv-template');
      const blob = new Blob([r.data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = r.data.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download template'); }
  };

  return (
    <Card className="shadow-lg shadow-black/5">
      <CardHeader>
        <CardTitle className="text-xl">Build your procedure catalog</CardTitle>
        <CardDescription>
          Your catalog grounds the AI recommendations in your practice's own recovery protocols.
          Start from a specialty template, upload your own CSV of medical codes, or add procedures later.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {templates.map((t) => (
            <div key={t.id} className="rounded-xl border bg-card p-4 flex flex-col gap-2" data-testid={`signup-template-${t.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{t.label}</span>
                <Badge variant="secondary">{t.count} procedures</Badge>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">{t.description}</p>
              <p className="text-xs text-muted-foreground line-clamp-1">{t.procedure_names.slice(0, 3).join(' · ')}…</p>
              {applied.includes(t.id) ? (
                <Button size="sm" variant="secondary" disabled className="mt-1" data-testid={`signup-template-applied-${t.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Added</Button>
              ) : (
                <Button size="sm" variant="outline" className="mt-1" onClick={() => apply(t.id)} disabled={!!busyId} data-testid={`signup-template-apply-${t.id}`}>
                  {busyId === t.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Layers className="h-3.5 w-3.5 mr-1" />} Use template
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-dashed bg-secondary/40 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Upload className="h-4 w-4 text-primary" />
            <div className="flex-1 min-w-48">
              <p className="text-sm font-medium">Upload your own medical codes (CSV)</p>
              <p className="text-xs text-muted-foreground">Columns: name, icd10, cpt, recovery weeks per severity, follow-up cadence, AI notes.</p>
            </div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => importCsv(e.target.files?.[0])} data-testid="signup-csv-input" />
            <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="signup-csv-template-button">Download template</Button>
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="signup-csv-upload-button">
              {importing && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />} Upload CSV
            </Button>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="ghost" onClick={onNext} data-testid="signup-catalog-skip">Skip for now</Button>
          <Button className="flex-1" onClick={onNext} data-testid="signup-catalog-continue">Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TeamStep({ onFinish }) {
  const [role, setRole] = useState('doctor');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState([]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post('/invites', { role, email, invitee_name: name });
      setInvites((l) => [r.data, ...l]);
      setEmail(''); setName('');
      toast.success('Invite link created — copy and share it');
    } catch (err) {
      toast.error(errText(err, 'Could not create invite'));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (inv) => {
    const url = `${window.location.origin}${inv.link_path}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied');
    } catch (e) {
      toast.info(url);
    }
  };

  return (
    <Card className="shadow-lg shadow-black/5">
      <CardHeader>
        <CardTitle className="text-xl">Invite your team</CardTitle>
        <CardDescription>
          Create invite links for your doctors and nurses. Share links directly — automatic email invitations arrive with the email integration.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-[130px_1fr_1fr_auto] gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="signup-invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="doctor">Doctor</SelectItem>
                <SelectItem value="nurse">Medical staff</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Name (optional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dr. Alex Morgan" data-testid="signup-invite-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Email (optional)</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alex@yourpractice.com" data-testid="signup-invite-email-input" />
          </div>
          <Button type="submit" disabled={busy} data-testid="signup-invite-create-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create link'}
          </Button>
        </form>

        {invites.length > 0 && (
          <ul className="space-y-2" data-testid="signup-invite-list">
            {invites.map((inv) => (
              <li key={inv.id} className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2 text-sm">
                <Badge variant="secondary" className="capitalize">{inv.role}</Badge>
                <span className="flex-1 truncate text-muted-foreground">{inv.invitee_name || inv.email || 'Unnamed invite'}</span>
                <Button size="sm" variant="outline" onClick={() => copy(inv)} data-testid={`signup-invite-copy-${inv.id}`}><Copy className="h-3.5 w-3.5 mr-1" /> Copy link</Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex gap-3">
          <Button variant="ghost" onClick={onFinish} data-testid="signup-team-skip">Skip for now</Button>
          <Button className="flex-1" onClick={onFinish} data-testid="signup-team-continue">Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** The finish screen: the QR code and link patients use. The same card as
 *  Admin → Settings, so what they print here is what they will find later. */
function PatientLinkStep({ onFinish }) {
  const { practice, packetTypes } = useApp();
  return (
    <div className="space-y-4" data-testid="signup-patient-link-step">
      <PatientIntakeQR
        practice={practice}
        packetTypes={packetTypes || []}
        className="shadow-lg shadow-black/5"
        title="Your patient link is ready"
        intro="Print the QR for your front desk or send the link to a patient. Each scan starts a private form that lands in your staff queue — no patient account needed. It is always available under Admin → Settings."
      />
      <div className="rounded-xl bg-accent/60 text-accent-foreground p-4 flex items-start gap-3">
        <PartyPopper className="h-4 w-4 mt-0.5" />
        <p className="text-sm">You're all set. Users, invites and your catalog are in the Admin portal whenever you need them.</p>
      </div>
      <Button className="w-full" onClick={onFinish} data-testid="signup-finish-button">Go to my dashboard <ArrowRight className="h-4 w-4 ml-1" /></Button>
    </div>
  );
}
